// src/store/slices/promoTelegramSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import promoterApi from '../../api/promouterApi';
import { reAuth } from '../../api/apiClient';
import avatarPlaceholder from '../../assets/avatar/avatart-male.png';
import telegramIcon from '../../assets/img/social/telegram.png';

const BASE_URL = 'https://api.duotetra.quest';

function buildLogoUrl(logoPath) {
    const path = String(logoPath || '').trim();
    if (!path) return null;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/')) return `${BASE_URL}${path}`;
    return `${BASE_URL}/${path}`;
}

/**
 * Avatar rules for promoter:
 * - no link yet → placeholder
 * - link set, no photo → Telegram logo
 * - photo returned → photo
 */
export function getPromoterAvatarSrc(link, data) {
    const logoUrl = buildLogoUrl(data?.logo || data?.logoPath);
    if (logoUrl) return logoUrl;

    const hasLink = Boolean(
        normalizeTelegramLink(link || '') ||
        normalizeTelegramLink(data?.link || '')
    );
    return hasLink ? telegramIcon : avatarPlaceholder;
}

export function normalizeTelegramLink(rawLink) {
    let trimmed = (rawLink || '').trim();
    if (!trimmed) return '';

    // алиасы Telegram (telegram.me / telegram.dog / www.) → t.me
    trimmed = trimmed
        .replace(/^https?:\/\/(www\.)?telegram\.me\//i, 'https://t.me/')
        .replace(/^https?:\/\/(www\.)?telegram\.dog\//i, 'https://t.me/')
        .replace(/^https?:\/\/www\.t\.me\//i, 'https://t.me/');

    if (trimmed.startsWith('https://t.me/s/')) {
        return trimmed.replace('https://t.me/s/', 'https://t.me/');
    }
    if (trimmed.startsWith('http://t.me/s/')) {
        return trimmed.replace('http://t.me/s/', 'https://t.me/');
    }
    if (trimmed.startsWith('@')) {
        return `https://t.me/${trimmed.slice(1)}`;
    }
    if (!trimmed.startsWith('http')) {
        return `https://t.me/${trimmed}`;
    }
    return trimmed;
}

export function isTelegramPromoterLink(rawLink) {
    const cleanLink = normalizeTelegramLink(rawLink);
    if (!cleanLink) return false;

    try {
        const url = new URL(cleanLink);
        const host = url.hostname.toLowerCase().replace(/^www\./, '');
        if (host !== 't.me' && host !== 'telegram.me' && host !== 'telegram.dog') return false;

        const path = url.pathname.replace(/^\//, '');
        if (!path) return false;

        if (path.startsWith('+') || path.startsWith('joinchat/') || /^c\/\d+/.test(path)) {
            return path.length > 2;
        }

        const username = path.split('/')[0];
        return /^[a-zA-Z][a-zA-Z0-9_]{3,31}$/.test(username);
    } catch {
        return false;
    }
}

export const TELEGRAM_LINK_ONLY_MESSAGE = 'Only Telegram links are allowed: channel / group / bot / account.';
export const TELEGRAM_NOT_FOUND_MESSAGE = 'Telegram account / channel / bot not found.';
export const TELEGRAM_AUTH_MESSAGE = 'Session expired. Close the mini-app and open it again from Telegram.';

export function getPromoterLinkDisplay(link, data, placeholder = 'Promoter link') {
    if (!link || link === placeholder) return placeholder;

    const normalizedLink = normalizeTelegramLink(link);
    const normalizedDataLink = data?.link ? normalizeTelegramLink(data.link) : '';

    if (data?.name && normalizedDataLink && normalizedDataLink === normalizedLink) {
        return getPromoterDisplayName(data) || data.name;
    }

    return link;
}

const CHANNEL_TYPES = new Set(['channel', 'supergroup', 'group']);
const PROFILE_TYPES = new Set(['user', 'profile', 'private', 'bot']);

function getPromoterPersonName(promoter) {
    const firstName = promoter.firstName || promoter.first_name || promoter.user?.firstName || promoter.user?.first_name || '';
    const lastName = promoter.lastName || promoter.last_name || promoter.user?.lastName || promoter.user?.last_name || '';
    return `${lastName} ${firstName}`.trim();
}

function inferPromoterType(promoter) {
    const explicitType = promoter.type || promoter.entityType || promoter.chatType || promoter.chat_type;
    if (explicitType) return String(explicitType).toLowerCase();

    const link = promoter.link || '';
    if (/\/c\/|\/\+/.test(link)) return 'channel';

    if (getPromoterPersonName(promoter)) return 'user';

    return '';
}

export function getPromoterDisplayName(promoter) {
    if (!promoter) return '';

    const type = inferPromoterType(promoter);
    const personName = getPromoterPersonName(promoter);
    const titleName = promoter.name || promoter.title || '';

    if (CHANNEL_TYPES.has(type)) {
        return titleName || promoter.username || personName || '';
    }

    if (PROFILE_TYPES.has(type)) {
        return personName || titleName || promoter.username || '';
    }

    return titleName || personName || promoter.username || '';
}

function unwrapMetaPayload(raw) {
    if (raw == null) return null;
    if (typeof raw !== 'object') return null;

    const nested = raw.data ?? raw.result ?? raw.promoter ?? raw.meta;
    if (nested === null) return null;
    if (nested && typeof nested === 'object') {
        const looksLikeMeta = nested.name != null
            || nested.title != null
            || nested.logo != null
            || nested.logoPath != null
            || nested.firstName != null
            || nested.first_name != null
            || nested.username != null
            || nested.type != null;
        if (looksLikeMeta) return nested;
    }

    return raw;
}

// Асинхронный thunk для синхронизации Telegram-меты
export const syncTelegramMeta = createAsyncThunk(
    'promoTelegram/syncTelegramMeta',
    async (rawLink, thunkAPI) => {
        try {
            const cleanLink = normalizeTelegramLink(rawLink);

            if (!cleanLink.startsWith('https://t.me/')) {
                return thunkAPI.rejectWithValue(TELEGRAM_LINK_ONLY_MESSAGE);
            }

            let raw;
            try {
                raw = await promoterApi.syncTelegramMeta(cleanLink);
                console.log(raw)
            } catch (firstErr) {
                if (firstErr?.response?.status !== 401) throw firstErr;
                await reAuth();
                raw = await promoterApi.syncTelegramMeta(cleanLink);
            }

            const data = unwrapMetaPayload(raw);

            if (!data) {
                return thunkAPI.rejectWithValue(TELEGRAM_NOT_FOUND_MESSAGE);
            }

            return {
                name: data.name || data.title || data.firstName || data.first_name || 'Unknown',
                title: data.title || data.name || null,
                firstName: data.firstName || data.first_name || null,
                lastName: data.lastName || data.last_name || null,
                logo: buildLogoUrl(data.logo || data.logoPath),
                link: cleanLink,
                type: data.type || data.entityType || data.chatType || data.chat_type || null,
                username: data.username || null,
            };
        } catch (error) {
            console.error('Ошибка синхронизации Telegram меты:', error);
            const status = error.response?.status;
            if (status === 401 || error?.message === 'NO_INIT_DATA') {
                return thunkAPI.rejectWithValue(TELEGRAM_AUTH_MESSAGE);
            }
            const errorMessage = status === 404
                ? TELEGRAM_NOT_FOUND_MESSAGE
                : (error.response?.data?.message || error.message || 'Ошибка синхронизации');
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

const promoTelegramSlice = createSlice({
    name: 'promoTelegram',
    initialState: {
        link: '',      // введённая (нормализованная) ссылка
        data: null,    // { name, logo, link }
        loading: false,
        error: null,
    },
    reducers: {
        setLink(state, action) {
            state.link = action.payload;
            const nextLink = normalizeTelegramLink(action.payload);
            const syncedLink = state.data?.link ? normalizeTelegramLink(state.data.link) : '';
            if (state.data && nextLink !== syncedLink) {
                state.data = null;
            }
        },
        clearData(state) {
            state.link = '';
            state.data = null;
            state.error = null;
            state.loading = false;
        },
        // Установка данных промоутера напрямую (для режима редактирования)
        setPromoterData(state, action) {
            state.data = action.payload;
            if (action.payload?.link) {
                state.link = action.payload.link;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(syncTelegramMeta.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(syncTelegramMeta.fulfilled, (state, action) => {
                state.loading = false;
                state.data = {
                    name: action.payload.name,
                    title: action.payload.title,
                    firstName: action.payload.firstName,
                    lastName: action.payload.lastName,
                    logo: action.payload.logo,
                    link: action.payload.link,
                    type: action.payload.type,
                    username: action.payload.username,
                };
                state.link = action.payload.link;
            })
            .addCase(syncTelegramMeta.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
            });
    },
});

export const { setLink, clearData, setPromoterData } = promoTelegramSlice.actions;
export default promoTelegramSlice.reducer;
